<script setup>

// Check-out the following link for more information: https://github.com/DevExpress-Examples/devextreme-dropdownbox-filter-data-in-nested-widget/blob/19.2.3%2B/Vue/src/components/DropDownBoxWithDataGrid.vue

import { ref, onMounted, nextTick } from 'vue'

import ReferenceLink from '@/components/shared/ReferenceLink.vue'
import InformationToolTip from './InformationToolTip.vue'

import {
  DxDataGrid,
  DxSelection,
  DxScrolling,
  DxFilterRow
} from 'devextreme-vue/data-grid'
import DxDropDownBox from 'devextreme-vue/drop-down-box'
import DxValidator from 'devextreme-vue/validator'
import DataSource from 'devextreme/data/data_source'

import { useDefaultPaging } from '@/composables/data-grid'
import { useFocusRestore } from '@/composables/focus-restore'

const props = defineProps({
  value: {
    type: Number,
    default: null,
  },
  dataSource: {
    type: Object,
    default: () => {},
  },
  columns: {
    type: Array,
    default: () => [],
  },
  columnChooser: {
    type: Object,
    default: () => { return { enabled: false } },
  },
  showClearButton: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  remoteOperations: {
    type: Boolean,
    default: true,
  },
  dropDownOptions: {
    type: Object,
    default: () => {
      return { width: '30rem', height: '25rem' }
    },
  },
  dropDownDisplayExpr: {
    type: Function,
    default: (obj) => {
      if (!obj) return ''

      if (typeof obj === 'string') return obj

      return `${obj.code} - ${obj.name}`
    }
  },
  rowPrepared: {
    type: Function,
    default: () => {},
  },
  cellPrepared: {
    type: Function,
    default: () => {},
  },
  searchTimeout: {
    type: Number,
    default: 500,
  },
  resolveSearchOptions: {
    type: Function,
    default: null,
  },
  minSearchLength: {
    type: Number,
    default: null,
  },
  openOnFieldClick: {
    type: Boolean,
    default: true,
  },
  selectFocusedRowOnTab: {
    type: Boolean,
    default: false,
  },
  paging: {
    type: Object,
    default: () => useDefaultPaging,
  },
  options: {
    type: Object,
    default: null
  },
  validationGroup: {
    type: String,
    default: undefined,
  },
  validationRules: {
    type: Array,
    default: () => [],
  },
})

const emits = defineEmits(['valueChanged', 'initialized', 'searchValueChanged'])

let dropDown = null
let dataGrid = null
const focusedRowKey = ref(props.value)
const dropDownOpened = ref(false)
const dropDownValue = ref(props.value)
const focusedRowIndex = ref(0)
const isLoading = ref(false)

const originalDataSourceLoad = props.dataSource.load

const dropDownRef = ref(null)
const { rootRef } = useFocusRestore()

onMounted(() => {
  dropDown = dropDownRef.value?.instance || dropDownRef.value
})

const focus = () => { dropDown?.focus ? dropDown.focus() : dropDownRef.value?.$el?.focus() }

defineExpose({ focus })

const onTabKeyDown = (e) => {
  if (e.key !== 'Tab' || props.selectFocusedRowOnTab) return
  e.stopPropagation()
  dropDown.close()
  dropDown.focus()
}

const onDropDownBoxInitialized = (e) => {
  dropDown = e.component
  e.element.addEventListener('keydown', onTabKeyDown, true)
  emits('initialized', e)
}

const onDataGridInitialized = (e) => {
  dataGrid = e.component
  e.element.addEventListener('keydown', onTabKeyDown, true)
}

const dropDownBoxDataSource = new DataSource({
  store: props.dataSource.store(),
  searchExpr: ['code'],
  searchOperation: 'startswith',
})

const onDropDownKeyDown = (e) => {
  if (!isLoading.value) {
    if (e.event.key === 'ArrowDown' || e.event.key === 'ArrowUp') {
      if (!dropDownOpened.value) {
        dropDownOpened.value = true
        e.component.isKeyDown = true
      } else {
        dataGrid && dataGrid.focus()
      }
    } else if (e.event.code === 'Enter' || e.event.code === 'NumpadEnter') {
      e.event.preventDefault()
      setDropDownValue(focusedRowKey.value)
    } else if (e.event.code === 'Escape') {
      dropDownOpened.value = false
      dropDown.focus()
    } else if (e.event.key === 'Tab' && dropDownOpened.value) {
      e.event.preventDefault()
      dropDown.focus()
      if (props.dataSource?.items()?.length) {
        setDropDownValue(props.dataSource?.items()[0].id)
      }
    }
  }
}

const onGridSelectionChanged = (e) => {
  if (e.selectedRowKeys.length > 0 && dropDownValue.value !== e.selectedRowKeys[0]) {
    setDropDownValue(e.selectedRowKeys[0])
  }
}

// Notice that contentReadyHandler will be called twice: first time when the grid is loaded with empty data, second time when the data is loaded (after dataSource.load() is called)
// In order to avoid focusing the grid before data is loaded, we use isNotFirstLoad flag
// Then we use a timer to delay the focus to ensure that the grid is fully rendered
const onDropDownOpened = (e) => {
  const ddbInstance = e.component
  if (ddbInstance.isKeyDown) {
    var contentReadyHandler = (args) => {
      if (dataGrid.isNotFirstLoad) {
        setTimeout(() => dataGrid.focus(), 250) // delay to ensure that the grid is fully rendered
        dataGrid.off('contentReady', contentReadyHandler)
      }
    }
    if (!dataGrid.isNotFirstLoad) {
      dataGrid.getDataSource().load().done(() => {
        dataGrid.isNotFirstLoad = true
      })
      dataGrid.on('contentReady', contentReadyHandler)
    } else {
      var optionChangedHandler = (args) => {
        const gridInstance = args.component
        if (args.name === 'focusedRowKey' || args.name === 'focusedColumnIndex') {
          gridInstance.off('optionChanged', optionChangedHandler)
          gridInstance.focus()
        }
      }
      dataGrid.on('optionChanged', optionChangedHandler)
      focusedRowIndex.value = 0
    }
    ddbInstance.isKeyDown = false
  } else {
    nextTick(() => ddbInstance.focus())
    if (dataGrid.isNotFirstLoad && isSearchIncomplete(ddbInstance)) {
      props.dataSource.load().done(items => {
        if (items.length > 0) {
          focusedRowKey.value = items[0].id
        }
        ddbInstance.focus()
      })
    }
  }
}

const onDropDownClosed = (e) => {
  const ddbInstance = e.component
  const searchValue = props.dataSource.searchValue()
  if (isSearchIncomplete(ddbInstance)) {
    dropDownValue.value = null
  }
  if (searchValue) {
    props.dataSource.searchValue(null)
  }
  lastSearchValue.value = null
  if (!dropDownValue.value || dropDown.option('text') === '') {
    dropDownValue.value = null
    dropDown.reset()
  }
}

let searchTimer = null

const isSearchIncomplete = (dropDownBox) => {
  // compare the last displayed value and the current real text in the input field
  let displayValue = dropDownBox.option('displayValue')
  let text = dropDownBox.option('text')
  text = text && text.length && text
  displayValue = displayValue && displayValue.length && displayValue[0]
  return text !== displayValue
}

const lastSearchValue = ref(null)

const onDropDownInput = (e) => {
  clearTimeout(searchTimer)
  if (props.minSearchLength && e.component.option('text').length < props.minSearchLength) {
    dropDownValue.value = null
    return
  }
  searchTimer = setTimeout(() => {
    const text = e.component.option('text')
    emits('searchValueChanged', text)
    const searchOptions = props.resolveSearchOptions?.(text)
    if (searchOptions) {
      props.dataSource.searchExpr(searchOptions.searchExpr)
      props.dataSource.searchOperation(searchOptions.searchOperation)
      props.dataSource.searchValue(searchOptions.searchValue)
    } else {
      props.dataSource.searchValue(text)
    }
    if (dropDownOpened.value && isSearchIncomplete(e.component) && lastSearchValue.value !== text) {
      props.dataSource.load().done((items) => {
        lastSearchValue.value = text
        if (items.length > 0 && dataGrid) {
          focusedRowKey.value = items[0].id
        }
      })
    } else if (text && text.length > 0) {
      dropDownOpened.value = true
    }
  }, props.searchTimeout)
}

const onDropDownValueChanged = (e) => {
  clearTimeout(searchTimer)
  const currentData = e.value ? props.dataSource.items().find((item) => item.id === e.value) ?? props.dataSource.items()[0] : null
  emits('valueChanged', currentData)
  dropDownValue.value = e.value
  dropDownOpened.value = false
  dropDown && dropDown.focus()
}

const onLostFocusOrFocusOut = (e) => {
  clearTimeout(searchTimer)
  if (dropDown.option('text') === '' && dropDownValue.value !== null) {
    dropDownValue.value = null
    emits('valueChanged', dropDownValue.value ? dropDownValue.value : null)
  }
}

// If a value is not selected in dropdown, the value is set to null
// const onDropDownFocusOut = (e) => {
//   if (dropDownOpened.value === false && (!props.value || dropDown.option('text') === '')) {
//     dropDown.reset()
//   }
// }

const setDropDownValue = (value) => {
  if (!dataGrid.getDataSource()?.items()?.length) {
    focusedRowKey.value = null
  }
  dropDownValue.value = value
  dropDownOpened.value = false
  dropDown && dropDown.focus()
}

const onDataGridKeyDown = (e) => {
  if (e.event.key === 'Enter') {
    e.event.preventDefault()
    setDropDownValue(focusedRowKey.value)
  } else if (e.event.code === 'Escape') {
    e.event.stopPropagation()
    dropDown.focus()
  } else if (e.event.key === 'Tab') {
    e.event.preventDefault()
    setDropDownValue(focusedRowKey.value)
  }
}

const onDataGridContentReady = (e) => {
  if (!e.component.isNotFirstLoad) {
    e.component.isNotFirstLoad = true
    dropDown.focus()
  }
}

const onRowPrepared = (e) => {
  if (props.rowPrepared) {
    props.rowPrepared(e)
  }
}

const onCellPrepared = (e) => {
  if (props.cellPrepared) {
    props.cellPrepared(e)
  }
}

props.dataSource.load = (...args) => {
  isLoading.value = true
  return originalDataSourceLoad.apply(props.dataSource, args)
    .then((res) => {
      isLoading.value = false
      return res
    })
    .catch((error) => {
      isLoading.value = false

      if (error !== 'canceled') { // Devextreme components always throw 'canceled' error on new search or component destroy, thus we ignore it
        throw error
      }
    })
}

const onEditorPreparing = (e) => {
  if (e.parentType === 'filterRow') e.showAllText = ''
}
</script>

<template>
  <div
    ref="rootRef"
    style="display: flex; align-items: center;"
  >
    <dx-drop-down-box
      ref="dropDownRef"
      v-model:opened="dropDownOpened"
      v-model:value="dropDownValue"
      value-change-event=""
      value-expr="id"
      content-template="contentTemplate"
      show-borders
      auto-navigate-to-focused-row
      :open-on-field-click="props.openOnFieldClick"
      :disabled="props.disabled"
      :drop-down-options="dropDownOptions"
      :data-source="dropDownBoxDataSource"
      :focused-row-enabled="false"
      :accept-custom-value="true"
      :show-clear-button="showClearButton"
      :display-expr="dropDownDisplayExpr"
      style="flex-grow: 1;"
      @input="onDropDownInput"
      @initialized="onDropDownBoxInitialized"
      @opened="onDropDownOpened"
      @closed="onDropDownClosed"
      @key-down="onDropDownKeyDown"
      @value-changed="onDropDownValueChanged"
      @lost-focus="onLostFocusOrFocusOut"
      @focus-out="onLostFocusOrFocusOut"
    >
      <dx-validator
        v-if="props.validationRules.length"
        :validation-group="props.validationGroup"
        :validation-rules="props.validationRules"
      />
      <template #contentTemplate>
        <dx-data-grid
          v-model:focused-row-key="focusedRowKey"
          v-model:focused-row-index="focusedRowIndex"
          height="100%"
          :sync-lookup-filter-values="false"
          :column-chooser="columnChooser"
          :data-source="props.dataSource"
          :columns="columns"
          :hover-state-enabled="true"
          :focused-row-enabled="true"
          :auto-navigate-to-focused-row="true"
          :remote-operations="props.remoteOperations"
          :paging="props.paging"
          @key-down="onDataGridKeyDown"
          @initialized="onDataGridInitialized"
          @row-prepared="onRowPrepared"
          @cell-prepared="onCellPrepared"
          @content-ready="onDataGridContentReady"
          @selection-changed="onGridSelectionChanged"
          @editor-preparing="onEditorPreparing"
        >
          <dx-filter-row :visible="true" />
          <dx-scrolling mode="virtual" />
          <dx-selection mode="single" />
          <template
            v-for="col in columns"
            #[`cellTemplate-${col.dataField}`]="slotProps"
          >
            <slot
              :name="`cellTemplate-${col.dataField}`"
              v-bind="slotProps"
            />
          </template>
        </dx-data-grid>
      </template>
    </dx-drop-down-box>
    <div
      v-if="props.options?.extractInfo"
    >
      <information-tool-tip
        :data="props.options.extractInfo"
        :options="{ showMobileInfo: true }"
      />
    </div>
    <div>
      <reference-link
        v-if="props.options?.routeParam"
        :data="{
          code: props.options?.routeParam,
        }"
        :query-fields="['code']"
        :target="props.options?.target"
        :visible="true"
      />
    </div>
  </div>
</template>
